import DeliveryAttempts from './DeliveryAttempts';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { apiUrl } from "../../config/api";
import MetaMessages from "./MetaMessages";
import "./websiteInquiries.css";

function getSessionHeaders() {
  const session = JSON.parse(localStorage.getItem("sessionData") || "{}");
  return {
    Authorization: session.sessionKey || "",
    Username: session.username || "",
    "Content-Type": "application/json",
  };
}

function formatDate(value) {
  if (!value) return "";
  return new Date(value).toLocaleString([], {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function WebsiteInquiries({ campaignId = "", onChanged = null }) {
  const params = useParams();
  const [source, setSource] = useState("form");
  const [inquiries, setInquiries] = useState([]);
  const [selectedId, setSelectedId] = useState(params.id || "");
  const [detail, setDetail] = useState(null);
  const [reply, setReplyValue] = useState("");
  const replyRevisionRef = useRef(0);
  const setReply = useCallback((value) => { replyRevisionRef.current += 1; setReplyValue(value); }, []);
  const replyAttemptRef = useRef(null);
  const replySendingRef = useRef(false);
  const [pendingReply, setPendingReply] = useState(null);
  const [reconcileEvidence, setReconcileEvidence] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [events, setEvents] = useState([]);
  const [gmailStatus, setGmailStatus] = useState(null);
  const [connectingGmail, setConnectingGmail] = useState(false);
  // Only the latest conversation request may fill the panel: a slow answer for the inquiry you
  // just left must never appear under the one you opened (and receive its reply).
  const detailRequestRef = useRef(0);
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;

  const selectedInquiry = useMemo(
    () => inquiries.find((item) => String(item.id) === String(selectedId)),
    [inquiries, selectedId]
  );
  const visibleInquiries = useMemo(() => {
    if (!campaignId) return inquiries;
    return inquiries.filter((inquiry) => {
      const inquiryCampaignId = inquiry.marketingAttribution?.campaign_id || "unattributed";
      return inquiryCampaignId === campaignId;
    });
  }, [campaignId, inquiries]);

  async function loadInquiries() {
    setError("");
    try {
      const response = await fetch(apiUrl("/website-inquiries"), {
        headers: getSessionHeaders(),
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.message || "No se pudieron cargar los mensajes.");
      setInquiries(data.inquiries || []);
      setSelectedId((current) => current || data.inquiries?.[0]?.id || "");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadEvents() {
    try {
      const response = await fetch(apiUrl("/events"), { headers: getSessionHeaders() });
      const data = await response.json();
      if (Array.isArray(data)) setEvents(data);
    } catch (err) {
      console.error("No se pudieron cargar los eventos para vincular:", err);
    }
  }

  async function loadGmailStatus() {
    try {
      const response = await fetch(apiUrl('/website-inquiries/gmail/status'), {
        headers: getSessionHeaders(),
      });
      const data = await response.json();
      if (data.success) setGmailStatus(data);
    } catch (err) {
      console.error('No se pudo consultar Gmail:', err);
    }
  }

  async function connectGmail() {
    setConnectingGmail(true);
    setError('');
    try {
      const response = await fetch(apiUrl('/website-inquiries/gmail/oauth/start'), {
        method: 'POST',
        headers: getSessionHeaders(),
        body: '{}',
      });
      const data = await response.json();
      if (!data.success || !data.authorizationUrl) {
        throw new Error(data.message || 'No se pudo iniciar la autorizacion de Gmail.');
      }
      window.location.assign(data.authorizationUrl);
    } catch (err) {
      setError(err.message);
      setConnectingGmail(false);
    }
  }

  async function loadDetail(id) {
    const requestId = detailRequestRef.current + 1;
    detailRequestRef.current = requestId;
    if (!id) {
      setDetail(null);
      return;
    }

    try {
      const response = await fetch(apiUrl(`/website-inquiries/${id}`), {
        headers: getSessionHeaders(),
      });
      const data = await response.json();
      if (requestId !== detailRequestRef.current) return;
      if (!data.success) throw new Error(data.message || "No se pudo cargar la conversacion.");
      setDetail({ ...data, inquiryId: String(id) });
    } catch (err) {
      if (requestId === detailRequestRef.current) setError(err.message);
    }
  }

  useEffect(() => {
    loadInquiries();
    loadEvents();
    loadGmailStatus();
  }, []);

  useEffect(() => {
    setDetail(null);
    setReply("");
    loadDetail(selectedId);
  }, [selectedId, setReply]);

  const detailMatchesSelection = Boolean(detail) && String(detail.inquiryId) === String(selectedId);

  useEffect(() => {
    if (!campaignId) return;
    const firstMatch = visibleInquiries[0]?.id || "";
    if (!visibleInquiries.some((item) => String(item.id) === String(selectedId))) {
      setSelectedId(firstMatch);
    }
  }, [campaignId, selectedId, visibleInquiries]);

  async function updateInquiry(id, payload) {
    const response = await fetch(apiUrl(`/website-inquiries/${id}`), {
      method: "PATCH",
      headers: getSessionHeaders(),
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    if (!data.success) throw new Error(data.message || "No se pudo actualizar.");
    await loadInquiries();
    // Refresh whichever conversation is open now (the user may have moved on meanwhile).
    await loadDetail(selectedIdRef.current);
    if (onChanged) {
      Promise.resolve(onChanged({ type: "inquiry-updated", inquiryId: id, payload }))
        .catch((err) => console.error("No se pudo refrescar Marketing:", err));
    }
  }

  async function sendReply() {
    if (!selectedId || !reply.trim() || !detailMatchesSelection || replySendingRef.current) return;
    // The reply belongs to the inquiry open when "Responder" was pressed; the user may open
    // another one while it is being sent, and that one's draft must stay untouched.
    const targetId = selectedId;
    const stillOpen = () => String(selectedIdRef.current) === String(targetId);
    const sentText = reply.trim();
    const draftRevision = replyRevisionRef.current;
    const username = getSessionHeaders().Username;
    const storageKey = `ovm_reply_attempt_${username}_${targetId}`;
    let attempt = replyAttemptRef.current?.storageKey === storageKey ? replyAttemptRef.current : null;
    if (!attempt) { try { attempt = JSON.parse(localStorage.getItem(storageKey) || 'null'); } catch { /* memory fallback */ } }
    if (attempt && attempt.bodyText !== sentText) {
      setPendingReply(attempt);
      setError('Verifica la respuesta anterior en el correo antes de iniciar otra.');
      return;
    }
    if (!attempt) attempt = { key: `reply-${window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`}`, bodyText: sentText, inquiryId: targetId, storageKey };
    replyAttemptRef.current = attempt;
    try {
      const encoded = JSON.stringify(attempt);
      localStorage.setItem(storageKey, encoded);
      if (localStorage.getItem(storageKey) !== encoded) throw new Error('Storage unavailable');
    } catch {
      setError('Habilita el almacenamiento del navegador antes de enviar la respuesta para poder recuperar su resultado.');
      return;
    }
    replySendingRef.current = true;
    setSending(true);
    setError("");
    try {
      const response = await fetch(apiUrl(`/website-inquiries/${targetId}/reply`), {
        method: "POST",
        headers: getSessionHeaders(),
        body: JSON.stringify({ bodyText: sentText, operationKey: attempt.key }),
      });
      const data = await response.json();
      if (!data.success) {
        if (data.pending) setPendingReply(attempt);
        if (data.retrySafe === true) {
          // Only a definite non-delivery can retire the old text and allow an edited reply.
          try { localStorage.removeItem(storageKey); } catch { /* memory fallback */ }
          replyAttemptRef.current = null;
          setPendingReply(null);
        }
        throw new Error(data.message || "No se pudo enviar la respuesta.");
      }
      try { localStorage.removeItem(storageKey); } catch { /* memory fallback */ }
      replyAttemptRef.current = null;
      setPendingReply(null);
      // Clear only what was sent: a follow-up typed meanwhile (or another inquiry's draft) stays.
      if (stillOpen() && draftRevision === replyRevisionRef.current) setReply((current) => (current.trim() === sentText ? "" : current));
      await loadInquiries();
      await loadDetail(selectedIdRef.current);
      if (onChanged) {
        Promise.resolve(onChanged({ type: "reply-sent", inquiryId: targetId }))
          .catch((err) => console.error("No se pudo refrescar Marketing:", err));
      }
    } catch (err) {
      setError(stillOpen() ? err.message : `La respuesta anterior no se envió: ${err.message}`);
    } finally {
      replySendingRef.current = false;
      setSending(false);
    }
  }

  async function reconcileReply(outcome) {
    if (!pendingReply || String(pendingReply.inquiryId) !== String(selectedId) || replySendingRef.current || sending) return;
    const attempt = pendingReply;
    const targetId = String(attempt.inquiryId);
    const draftRevision = replyRevisionRef.current;
    const stillOpen = () => String(selectedIdRef.current) === targetId;
    replySendingRef.current = true;
    setSending(true);
    try {
      const response = await fetch(apiUrl(`/website-inquiries/${targetId}/reply-attempts/${encodeURIComponent(attempt.key)}/reconcile`), {
        method: 'POST', headers: getSessionHeaders(), body: JSON.stringify({ outcome, evidence: reconcileEvidence }),
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.message || 'No se pudo verificar el envío.');
      try { localStorage.removeItem(attempt.storageKey); } catch { /* memory fallback */ }
      if (replyAttemptRef.current?.key === attempt.key) replyAttemptRef.current = null;
      if (data.sent && stillOpen() && draftRevision === replyRevisionRef.current) {
        setReply((current) => current.trim() === attempt.bodyText ? '' : current);
      }
      setPendingReply((current) => current?.key === attempt.key ? null : current);
      if (stillOpen()) {
        setReconcileEvidence('');
        setError(data.sent ? 'Envío registrado sin mandar otra copia.' : 'Verificación guardada. Puedes editar o reintentar la respuesta.');
      }
      await loadDetail(selectedIdRef.current);
    } catch (err) { if (stillOpen()) setError(err.message); }
    finally { replySendingRef.current = false; setSending(false); }
  }

  if (!campaignId && source !== "form") {
    return (
      <div className="oliva-message-sources">
        <nav className="oliva-message-sources__tabs" aria-label="Canal de mensajes">
          <button type="button" onClick={() => setSource("form")}>Formulario</button>
          <button type="button" className={source === "instagram" ? "is-active" : ""} onClick={() => setSource("instagram")}>Instagram</button>
          <button type="button" className={source === "whatsapp" ? "is-active" : ""} onClick={() => setSource("whatsapp")}>WhatsApp</button>
        </nav>
        <MetaMessages channel={source} />
      </div>
    );
  }

  return (
    <div className="oliva-message-sources">
      {!campaignId ? <nav className="oliva-message-sources__tabs" aria-label="Canal de mensajes">
        <button type="button" className="is-active" onClick={() => setSource("form")}>Formulario</button>
        <button type="button" onClick={() => setSource("instagram")}>Instagram</button>
        <button type="button" onClick={() => setSource("whatsapp")}>WhatsApp</button>
      </nav> : null}
    <div className="site-messages">
      <section className="site-messages__list" aria-label="Mensajes del sitio">
        <div className="site-messages__list-top">
          <div>
            <span className="site-messages__badge">Oliva y Miel</span>
            <h1>Mensajes</h1>
            {campaignId ? <small>Filtrados por campaña: {campaignId === "unattributed" ? "Sin atribución" : campaignId}</small> : null}
          </div>
          <div className="site-messages__toolbar">
            <button
              type="button"
              className={gmailStatus?.configured ? 'is-connected' : ''}
              onClick={connectGmail}
              disabled={connectingGmail}
              title={gmailStatus?.configured
                ? `Gmail conectado: ${gmailStatus.accountEmail}`
                : 'Conectar buzon central de Gmail'}
            >
              <span className="material-symbols-outlined">mail</span>
              {gmailStatus?.configured ? 'Gmail' : 'Conectar'}
            </button>
            <button type="button" onClick={() => { loadInquiries(); loadGmailStatus(); }} title="Actualizar">
              <span className="material-symbols-outlined">refresh</span>
            </button>
          </div>
        </div>

        {loading ? <p className="site-messages__empty">Cargando mensajes...</p> : null}
        {!loading && visibleInquiries.length === 0 ? <p className="site-messages__empty">No hay mensajes para esta selección.</p> : null}

        <div className="site-messages__items">
          {visibleInquiries.map((inquiry) => (
            <button
              type="button"
              key={inquiry.id}
              className={`site-messages__item ${String(selectedId) === String(inquiry.id) ? "is-active" : ""}`}
              onClick={() => setSelectedId(inquiry.id)}
            >
              <span className={`site-messages__dot ${inquiry.isUnread ? "is-unread" : ""}`} />
              <span className="site-messages__item-main">
                <strong>{inquiry.contactName}</strong>
                <small>{inquiry.eventType} · {inquiry.participants} personas</small>
              </span>
              <span className="site-messages__item-meta">
                {inquiry.unreadCount ? <span>{inquiry.unreadCount}</span> : null}
                <small>{formatDate(inquiry.lastMessageAt)}</small>
              </span>
            </button>
          ))}
        </div>
      </section>

      <section className="site-messages__detail" aria-label="Conversacion">
        {error ? <div className="site-messages__error">{error}</div> : null}

        {!selectedInquiry || !detailMatchesSelection ? (
          <div className="site-messages__placeholder">
            {selectedInquiry ? "Cargando conversacion..." : "Selecciona un mensaje para revisar la conversacion."}
          </div>
        ) : (
          <>
            <div className="site-messages__contact">
              <div>
                <span className="site-messages__badge">{selectedInquiry.status}</span>
                <h2>{selectedInquiry.contactName}</h2>
                <p>{selectedInquiry.contactEmail} · {selectedInquiry.contactPhone}</p>
              </div>
              <div className="site-messages__actions">
                <button type="button" onClick={() => updateInquiry(selectedId, { isUnread: false, lastMessageId: detail?.messages?.at(-1)?.id })}>
                  <span className="material-symbols-outlined">drafts</span>
                  Leido
                </button>
                <button type="button" onClick={() => updateInquiry(selectedId, { archived: true, status: "cerrado" })}>
                  <span className="material-symbols-outlined">archive</span>
                  Cerrar
                </button>
              </div>
            </div>

            <div className="site-messages__meta">
              <span>{selectedInquiry.eventType}</span>
              <span>{selectedInquiry.participants} personas</span>
              <span>{selectedInquiry.eventDateText || "Sin fecha"}</span>
              <span>#{selectedInquiry.publicId}</span>
            </div>

            {Object.keys(selectedInquiry.marketingAttribution || {}).length ? (
              <div className="site-messages__attribution">
                <strong>Origen de campaña</strong>
                <span>{selectedInquiry.marketingAttribution.utm_source || "Fuente desconocida"}</span>
                <span>{selectedInquiry.marketingAttribution.utm_campaign || "Campaña sin nombre"}</span>
                {selectedInquiry.marketingAttribution.campaign_id ? <span>ID {selectedInquiry.marketingAttribution.campaign_id}</span> : null}
                {selectedInquiry.marketingAttribution.adset_id ? <span>Ad set {selectedInquiry.marketingAttribution.adset_id}</span> : null}
                {selectedInquiry.marketingAttribution.ad_id ? <span>Anuncio {selectedInquiry.marketingAttribution.ad_id}</span> : null}
              </div>
            ) : null}

            <div className="site-messages__event-link">
              <label htmlFor="inquiry-event-link">Venta/evento relacionado</label>
              <select
                id="inquiry-event-link"
                value={selectedInquiry.linkedEventId || ""}
                onChange={(event) => updateInquiry(selectedId, { linkedEventId: event.target.value ? Number(event.target.value) : null })}
              >
                <option value="">Sin vincular — todavía no es una venta</option>
                {events.map((item) => (
                  <option key={item.id} value={item.id}>
                    #{item.id} · {item.contact_name || item.event_type} · {formatDate(item.starts_at)} · {item.event_status}
                  </option>
                ))}
              </select>
              <small>La venta solo se contabiliza cuando el evento vinculado se marca manualmente como confirmado.</small>
            </div>

            <div className="site-messages__thread">
              {detail.messages.map((message) => (
                <article
                  key={message.id}
                  className={`site-messages__bubble site-messages__bubble--${message.direction}`}
                >
                  <div>
                    <strong>{message.senderName || message.senderEmail || message.source}</strong>
                    <small>{formatDate(message.createdAt)}</small>
                  </div>
                  <p>{message.bodyText}</p>
                </article>
              ))}
            </div>

            <DeliveryAttempts key={selectedId} attempts={detail.relayAttempts} endpoint={`/website-inquiries/${selectedId}/relay-attempts`} headers={getSessionHeaders} onResolved={() => loadDetail(selectedIdRef.current)} />
            {pendingReply && String(pendingReply.inquiryId) === String(selectedId) && <div className="site-messages__error">
              <p>Verifica en el correo si se envió esta respuesta antes de continuar: {pendingReply.bodyText}</p>
              <input aria-label="Evidencia de verificación del correo" value={reconcileEvidence} onChange={(event) => setReconcileEvidence(event.target.value)} placeholder="Cómo verificaste el envío" />
              <button type="button" disabled={sending || reconcileEvidence.trim().length < 10} onClick={() => reconcileReply('sent')}>Confirmé que se envió</button>
              <button type="button" disabled={sending || reconcileEvidence.trim().length < 10} onClick={() => reconcileReply('not_sent')}>Confirmé que no se envió</button>
            </div>}
            <div className="site-messages__composer">
              <textarea
                value={reply}
                onChange={(event) => setReply(event.target.value)}
                placeholder="Escribe una respuesta para enviar por correo..."
              />
              <button type="button" onClick={sendReply} disabled={sending || !reply.trim()}>
                <span className="material-symbols-outlined">send</span>
                {sending ? "Enviando" : "Responder"}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
    </div>
  );
}

export default WebsiteInquiries;
